import React, { useEffect, useState, ImgHTMLAttributes, ReactElement } from 'react'
import api from 'helpers/api'
import { isMortaUploadedUrl, validateSourceUrl } from 'helpers/utils'
import { ContentBlock, ContentState } from 'draft-js'
import { useApplicationStore } from 'hooks/application'

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  block: ContentBlock
  contentState: ContentState
}

export default React.forwardRef<HTMLImageElement, ImageProps>(function EmbeddedImage(props, ref): ReactElement {
  const { displayErrorMessage } = useApplicationStore()
  const { block, contentState, style, ...elementProps } = props

  const [src, setSrc] = useState<string | undefined>(
    contentState.getEntity(block.getEntityAt(0)).getData().src?.url ||
      contentState.getEntity(block.getEntityAt(0)).getData().src
  )
  const [ready, setReady] = React.useState(false)

  const signUrl = async () => {
    if (src) {
      try {
        const signedFileTokenResponse = await api.signUrl(src)
        setSrc(signedFileTokenResponse.data.url)
      } catch (e) {
        displayErrorMessage(e)
        setSrc(undefined)
      }
      setReady(true)
    }
  }

  useEffect(() => {
    if (src && isMortaUploadedUrl(src)) {
      signUrl().catch(() => {
        // Unhandled rejection is handled by displayErrorMessage,
        // this is just to prevent the console error.
      })
    } else {
      setReady(true)
    }
  }, [])

  if (!ready || !src) {
    return (
      <img
        ref={ref}
        style={{
          ...style,
          width: style?.width || '40%',
          minHeight: 60,
          objectFit: 'contain',
          background: 'var(--color-surface-sunken)'
        }}
        alt={!ready ? 'Loading image...' : 'Image not available'}
      />
    )
  }

  return (
    <img
      {...elementProps}
      ref={ref}
      src={validateSourceUrl(src)}
      role="presentation"
      style={style}
      alt={src || 'Embedded image'}
    />
  )
})
